/**
 * API 클라이언트 레이어
 *
 * VITE_API_MODE 환경변수로 호출 방식 전환:
 * - 'mock':      가짜 데이터 반환 (개발용)
 * - 'firestore': Firestore 직접 write (베타용)
 * - 'rest':      FastAPI Cloud Run 호출 (프로덕션)
 *
 * VITE_API_BASE_URL이 비어있으면 상대경로(/api/...)로 호출
 * → Firebase Hosting 리라이트가 Cloud Run으로 프록시
 */

import { IS_DEBUG } from '@/config/constants';
import type {
  Inquiry,
  InquiryListResponse,
  InquiryPayload,
  InquiryResponse,
} from '@/types/inquiry';

// ================================================================
// 에러 클래스
// ================================================================
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ================================================================
// API 모드
// ================================================================
type ApiMode = 'mock' | 'firestore' | 'rest';
const MODE: ApiMode = (import.meta.env.VITE_API_MODE as ApiMode | undefined) ?? 'mock';

function log(...args: unknown[]): void {
  if (IS_DEBUG) console.log('[API]', ...args);
}

export function getApiMode(): ApiMode {
  return MODE;
}

// ================================================================
// 공통 유틸: API base URL 결정
// ================================================================
function getBaseUrl(): string {
  const url = import.meta.env.VITE_API_BASE_URL as string | undefined;
  // 비어있으면 상대경로 → Firebase Hosting 리라이트가 Cloud Run으로 프록시
  return url && url.trim() !== '' ? url.trim() : '';
}

// ================================================================
// 공통 유틸: 인증 헤더 포함 fetch
// ================================================================
async function authFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  // Firebase Auth ID 토큰 가져오기
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new ApiError('로그인이 필요합니다.', 401, 'UNAUTHENTICATED');
  }

  const token = await user.getIdToken();
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${path}`;

  log('authFetch', options.method ?? 'GET', url);

  let res: Response;
  try {
    res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers ?? {}),
      },
    });
  } catch {
    throw new ApiError(
      '서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.',
      0,
      'NETWORK_ERROR'
    );
  }

  return res;
}

// ================================================================
// 공통 유틸: 응답 파싱
// ================================================================
async function parseResponse<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    let message = `서버 오류 (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.detail === 'string') message = body.detail;
    } catch {
      // 무시
    }
    throw new ApiError(message, res.status);
  }
  return (await res.json()) as T;
}

// ================================================================
// 접수 제출: Mock
// ================================================================
async function submitInquiryMock(payload: InquiryPayload): Promise<InquiryResponse> {
  log('Mock submitInquiry', payload);
  await new Promise((r) => setTimeout(r, 400 + Math.random() * 400));
  return { id: `mock_${Date.now()}`, status: 'pending' };
}

// ================================================================
// 접수 제출: Firestore 직접 write
// ================================================================

/**
 * undefined 값을 가진 키를 제거한다.
 *
 * [2026-09-02 P0 수정]
 *   Firestore addDoc() 은 undefined 값을 거부하고 예외를 던진다.
 *     "Unsupported field value: undefined (found in field contactEmail)"
 *   ignoreUndefinedProperties 도 설정돼 있지 않다.
 *
 *   그런데 두 폼 모두 빈 선택 항목을 undefined 로 변환해 보내고 있었다.
 *     B2BModal    contactEmail / employeeCount / contactSource
 *     InboundForm employeeCount
 *     예) contactEmail: data.contactEmail || undefined
 *
 *   → 고객이 이메일이나 직원수를 비우고 제출하면 접수 자체가 실패했다.
 *     사내 테스트에서는 모든 항목을 채워 넣어 드러나지 않았다.
 *
 *   폼마다 고치는 대신 write 직전 한 지점에서 막는다. 새 선택 필드가
 *   추가되어도 같은 사고가 재발하지 않는다.
 *   REST 경로는 JSON.stringify 가 undefined 키를 자동으로 빼므로 무관하다.
 *
 * 주의: null 은 유지한다. Firestore 에서 null 은 유효한 값이고
 *       "값이 비어 있음"을 명시하는 용도로 쓰일 수 있다.
 */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as Partial<T>;
}

async function submitInquiryFirestore(payload: InquiryPayload): Promise<InquiryResponse> {
  log('Firestore submitInquiry', payload);
  const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
  const { getDb } = await import('@/config/firebase');
  const db = await getDb();
  const docRef = await addDoc(collection(db, 'inquiries'), {
    ...stripUndefined(payload as unknown as Record<string, unknown>),
    status: 'pending',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: docRef.id, status: 'pending' };
}

// ================================================================
// 접수 제출: REST API
// ================================================================
async function submitInquiryRest(payload: InquiryPayload): Promise<InquiryResponse> {
  log('REST submitInquiry', payload);
  const baseUrl = getBaseUrl();
  let res: Response;
  try {
    res = await fetch(`${baseUrl}/api/v1/inquiries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new ApiError('서버에 연결할 수 없습니다.', 0, 'NETWORK_ERROR');
  }
  return parseResponse<InquiryResponse>(res);
}

// ================================================================
// 공개 API: 접수 제출
// ================================================================
export async function submitInquiry(payload: InquiryPayload): Promise<InquiryResponse> {
  switch (MODE) {
    case 'mock': return submitInquiryMock(payload);
    case 'firestore': return submitInquiryFirestore(payload);
    case 'rest': return submitInquiryRest(payload);
    default: {
      const _exhaustive: never = MODE;
      throw new ApiError(`알 수 없는 API 모드: ${_exhaustive}`);
    }
  }
}

// ================================================================
// Signed URL 발급
// ================================================================
export interface UploadUrlRequest {
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  filePath: string;
}

export async function getUploadUrl(req: UploadUrlRequest): Promise<UploadUrlResponse> {
  const baseUrl = getBaseUrl();
  const res = await fetch(`${baseUrl}/api/v1/inquiries/upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return parseResponse<UploadUrlResponse>(res);
}

// ================================================================
// 관리자 API: 접수 목록 (cursor 페이지네이션)
// ================================================================
export interface ListInquiriesParams {
  limit?: number;
  cursor?: string | null;
  serviceType?: string;
  status?: string;
  channel?: string;
}

export async function listInquiries(
  params: ListInquiriesParams = {}
): Promise<InquiryListResponse> {
  // Mock 모드
  if (MODE === 'mock') {
    log('Mock listInquiries', params);
    return { items: [], nextCursor: null, total: 0 };
  }

  // Firestore 직접 조회
  if (MODE === 'firestore') {
    return listInquiriesFirestore(params);
  }

  // REST 모드
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.serviceType) qs.set('service', params.serviceType);
  if (params.status) qs.set('status', params.status);
  if (params.channel) qs.set('channel', params.channel);

  const res = await authFetch(`/api/v1/admin/inquiries?${qs.toString()}`);
  return parseResponse<InquiryListResponse>(res);
}

// ================================================================
// Firestore 직접 조회 (관리자용 - firestore 모드)
// ================================================================
async function listInquiriesFirestore(
  params: ListInquiriesParams = {}
): Promise<InquiryListResponse> {
  const {
    collection,
    query,
    where,
    orderBy,
    limit,
    startAfter,
    getDoc,
    getDocs,
    doc,
  } = await import('firebase/firestore');
  const { getDb } = await import('@/config/firebase');
  const db = await getDb();

  const pageLimit = params.limit ?? 20;
  const constraints: Parameters<typeof query>[1][] = [
    orderBy('createdAt', 'desc'),
    limit(pageLimit + 1), // 다음 페이지 여부 확인용 +1
  ];

  if (params.serviceType && params.serviceType !== 'all') {
    constraints.unshift(where('serviceType', '==', params.serviceType));
  }
  if (params.status && params.status !== 'all') {
    constraints.unshift(where('status', '==', params.status));
  }
  if (params.channel && params.channel !== 'all') {
    constraints.unshift(where('channel', '==', params.channel));
  }

  // cursor 적용
  if (params.cursor) {
    try {
      const cursorDoc = await getDoc(doc(db, 'inquiries', params.cursor));
      if (cursorDoc.exists()) {
        constraints.push(startAfter(cursorDoc));
      }
    } catch {
      // cursor 무효 시 무시
    }
  }

  const q = query(collection(db, 'inquiries'), ...constraints);
  const snap = await getDocs(q);

  const hasMore = snap.docs.length > pageLimit;
  const docs = hasMore ? snap.docs.slice(0, pageLimit) : snap.docs;

  const items: Inquiry[] = docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<Inquiry, 'id'>),
  }));

  return {
    items,
    nextCursor: hasMore ? (docs[docs.length - 1]?.id ?? null) : null,
    total: items.length,
  };
}

// ================================================================
// 관리자 API: 접수 상세
// ================================================================
export async function getInquiry(id: string): Promise<Inquiry> {
  if (MODE === 'mock') {
    throw new ApiError('Mock 모드에서는 상세 조회가 지원되지 않습니다.');
  }

  if (MODE === 'firestore') {
    const { doc, getDoc } = await import('firebase/firestore');
    const { getDb } = await import('@/config/firebase');
    const db = await getDb();
    const snap = await getDoc(doc(db, 'inquiries', id));
    if (!snap.exists()) throw new ApiError('접수 건을 찾을 수 없습니다.', 404);
    return { id: snap.id, ...(snap.data() as Omit<Inquiry, 'id'>) };
  }

  const res = await authFetch(`/api/v1/admin/inquiries/${id}`);
  return parseResponse<Inquiry>(res);
}

// ================================================================
// 관리자 API: 상태 변경
// ================================================================
export async function updateInquiry(
  id: string,
  patch: Partial<Pick<Inquiry, 'status' | 'notes' | 'assignedTo'>>
): Promise<Inquiry> {
  if (MODE === 'firestore') {
    const { doc, updateDoc, serverTimestamp, getDoc } = await import('firebase/firestore');
    const { getDb } = await import('@/config/firebase');
    const db = await getDb();
    await updateDoc(doc(db, 'inquiries', id), {
      ...patch,
      updatedAt: serverTimestamp(),
    });
    const snap = await getDoc(doc(db, 'inquiries', id));
    return { id: snap.id, ...(snap.data() as Omit<Inquiry, 'id'>) };
  }

  const res = await authFetch(`/api/v1/admin/inquiries/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return parseResponse<Inquiry>(res);
}

// ================================================================
// 관리자 API: soft delete
// ================================================================
export async function deleteInquiry(id: string): Promise<void> {
  if (MODE === 'firestore') {
    const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
    const { getDb } = await import('@/config/firebase');
    const db = await getDb();
    await updateDoc(doc(db, 'inquiries', id), {
      deleted: true,
      updatedAt: serverTimestamp(),
    });
    return;
  }

  const res = await authFetch(`/api/v1/admin/inquiries/${id}`, { method: 'DELETE' });
  return parseResponse<void>(res);
}

// ================================================================
// 관리자 API: 대시보드 통계
// ================================================================
export interface DashboardStats {
  total: number;
  pending: number;
  contacted: number;
  inProgress: number;
  completed: number;
  cancelled: number;
  byService: Record<string, number>;
  todayCount: number;
  weekCount: number;
}

export async function getDashboardStats(): Promise<DashboardStats> {
  // Mock 모드
  if (MODE === 'mock') {
    return {
      total: 0, pending: 0, contacted: 0, inProgress: 0,
      completed: 0, cancelled: 0, byService: {}, todayCount: 0, weekCount: 0,
    };
  }

  // Firestore 직접 집계
  if (MODE === 'firestore') {
    return getDashboardStatsFirestore();
  }

  // REST 모드
  const res = await authFetch('/api/v1/admin/stats');
  return parseResponse<DashboardStats>(res);
}

// ================================================================
// Firestore 직접 통계 집계
// ================================================================
async function getDashboardStatsFirestore(): Promise<DashboardStats> {
  const { collection, query, where, orderBy, getDocs } = await import('firebase/firestore');
  const { getDb } = await import('@/config/firebase');
  const db = await getDb();

  // 전체 접수 조회 (최근 500건 기준)
  const { limit } = await import('firebase/firestore');
  const q = query(
    collection(db, 'inquiries'),
    where('deleted', '!=', true),
    orderBy('createdAt', 'desc'),
    limit(500)
  );

  const snap = await getDocs(q).catch(async () => {
    // 인덱스 없을 시 단순 조회 fallback
    const { getDocs: gd, collection: col } = await import('firebase/firestore');
    return gd(col(db, 'inquiries'));
  });

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekStart = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - 7);

  const stats: DashboardStats = {
    total: 0, pending: 0, contacted: 0, inProgress: 0,
    completed: 0, cancelled: 0, byService: {}, todayCount: 0, weekCount: 0,
  };

  snap.docs.forEach((d) => {
    const data = d.data();
    if (data.deleted) return;

    stats.total++;

    // 상태별 집계
    const status = data.status as string;
    if (status === 'pending') stats.pending++;
    else if (status === 'contacted') stats.contacted++;
    else if (status === 'in_progress') stats.inProgress++;
    else if (status === 'completed') stats.completed++;
    else if (status === 'cancelled') stats.cancelled++;

    // 서비스별 집계
    const svc = data.serviceType as string;
    if (svc) stats.byService[svc] = (stats.byService[svc] ?? 0) + 1;

    // 날짜별 집계
    const createdAt = data.createdAt as { toDate?: () => Date } | undefined;
    if (createdAt?.toDate) {
      const date = createdAt.toDate();
      if (date >= todayStart) stats.todayCount++;
      if (date >= weekStart) stats.weekCount++;
    }
  });

  return stats;
}

// ================================================================
// 관리자 API: CSV 내보내기 URL
// ================================================================
export async function getExportUrl(params: ListInquiriesParams = {}): Promise<string> {
  const qs = new URLSearchParams();
  if (params.serviceType) qs.set('service', params.serviceType);
  if (params.status) qs.set('status', params.status);

  const baseUrl = getBaseUrl();
  const { getAuth } = await import('firebase/auth');
  const token = await getAuth().currentUser?.getIdToken();

  // REST 모드에서만 지원
  return `${baseUrl}/api/v1/admin/export?${qs.toString()}&token=${token ?? ''}`;
}
